// Pure grouping/rollup helpers for the v2 pages. Ports of logic that lived
// inside legacy components (AppNameTile.getOverallStatus, JobIdModal's
// status-priority dedup, ReleasesLens' env-map grouping) so pages stay thin.

import { DeploymentRecord, NeedsAttentionConfig } from '../../types/DeploymentRecord';
import { shouldShowInNeedsAttention } from '../../utils/needsAttentionFilter';
import { parseTimestamp } from './format';

export type OverallStatusKey = 'deploying' | 'failures' | 'pending' | 'healthy' | 'mixed';

const IN_PROGRESS = ['inprogress', 'in_progress', 'in progress'];

export const isInProgressStatus = (status?: string): boolean =>
  IN_PROGRESS.includes(status?.toLowerCase() || '');

// Watch states where the post-deploy auto-rollback watch is still in flight.
// A deploy in one of these has finished deploying (status=success) but isn't
// settled yet — the watcher could still roll it back — so it counts as an
// active deploy until the watch reaches a terminal state. (DEVOPS-1629)
const ACTIVE_WATCH_STATES = ['pending', 'watching', 'rolling_back'];

/** True while a record's auto-rollback watch is still in flight. */
export const isActivelyWatching = (record: DeploymentRecord): boolean =>
  ACTIVE_WATCH_STATES.includes((record.watch_state || '').toLowerCase());

/**
 * A deploy still in flight — either the deploy itself is running, or its
 * post-deploy watch hasn't concluded. Use this (not bare `isInProgressStatus`)
 * for "active deploy" surfaces so a succeeded-but-watching deploy stays active
 * until the watch clears.
 */
export const isActiveDeploy = (record: DeploymentRecord): boolean =>
  isInProgressStatus(record.status) || isActivelyWatching(record);

// Terminal watch states that mean a rollback happened (or couldn't): the deploy
// was bad enough that the watcher reverted it (`rolled_back`) or tried and
// failed (`rollback_failed`, where the bad version may still be live). Both
// need a human to look — they are NOT "cleared/healthy". (DEVOPS-1629)
const ROLLBACK_PROBLEM_STATES = ['rolled_back', 'rollback_failed'];

/** True when a record's watch concluded in a rollback (or a failed rollback). */
export const isRollbackProblem = (record: DeploymentRecord): boolean =>
  ROLLBACK_PROBLEM_STATES.includes((record.watch_state || '').toLowerCase());

/**
 * Parse a timestamp to epoch ms for comparison/sorting. Missing or unparseable
 * values sort oldest (NEGATIVE_INFINITY) rather than producing NaN, which would
 * make `>` always false and `-` poison sort order non-deterministically.
 */
export const ts = (value?: string): number => {
  const t = value ? parseTimestamp(value).getTime() : Number.NaN;
  return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
};

// ---------------------------------------------------------------------------
// Stale / superseded current-state filtering
// ---------------------------------------------------------------------------
//
// A deploy that fails or stalls leaves a lingering `failed` / `in_progress`
// record that otherwise clutters the current-state surfaces (Deploying Now,
// the env matrix, Needs-attention) indefinitely. These helpers identify the
// records that should drop off. They never touch `success`/`pending` records,
// so "what is currently deployed" is always preserved.

/** Deploys older than this are considered stale and dropped (3 days). */
export const STALE_DEPLOY_MS = 3 * 24 * 60 * 60 * 1000;

const isFailed = (status?: string): boolean => status?.toLowerCase() === 'failed';
const isSuccess = (status?: string): boolean => status?.toLowerCase() === 'success';

/**
 * Strict production-env match. Unlike `isProductionEnvironment` (substring-
 * based), this only treats canonical prod names as prod — so `nonprod` /
 * `preprod` are NOT exempted from the staleness age-out below.
 */
const isProdEnv = (env: string): boolean => {
  const e = env.toLowerCase().trim();
  return e === 'prod' || e === 'production';
};

/** An unresolved current attempt — the only thing the drop-off logic targets. */
const isUnresolved = (status?: string): boolean =>
  isInProgressStatus(status) || isFailed(status);

/** Grouping key for "the same deploy target": app + service + env. */
const targetKey = (r: DeploymentRecord): string =>
  `${r.app || ''}:${r.serviceName || r.service || ''}:${r.envName || r.env || ''}`;

/**
 * Ids of `in_progress` / `failed` records that should be hidden from
 * current-state surfaces. A record is hidden when EITHER:
 *   - it is older than {@link STALE_DEPLOY_MS}, OR
 *   - a strictly-newer deploy for the same app+service+env has superseded it.
 *
 * Production failures are special-cased: a `failed` record on a prod env is the
 * true current state of prod, so it is exempt from the age cutoff and only
 * drops once the next *successful* prod deploy for that target supersedes it.
 *
 * `now` is injected for testability.
 */
export const staleCurrentIds = (
  records: DeploymentRecord[],
  now: number = Date.now()
): Set<string> => {
  // Bucket by deploy target so "newer attempt" comparisons stay within env.
  const byTarget = new Map<string, DeploymentRecord[]>();
  records.forEach(r => {
    const key = targetKey(r);
    const list = byTarget.get(key);
    if (list) list.push(r);
    else byTarget.set(key, [r]);
  });

  const hidden = new Set<string>();

  records.forEach(r => {
    if (!r.id || !isUnresolved(r.status)) return;

    const prodFailure = isFailed(r.status) && isProdEnv(r.envName || r.env || '');

    const group = byTarget.get(targetKey(r)) || [];
    const newer = group.filter(o => o !== r && ts(o.timestamp) > ts(r.timestamp));

    // Supersede — only the latest attempt per target matters:
    //   - prod failure: stays visible until prod is healthy again, so it clears
    //     only on a newer success OR a newer failure (the newer failure becomes
    //     the current prod-failure signal); a merely-running retry does NOT
    //     clear it.
    //   - everything else: any newer attempt (success, running, or failed)
    //     supersedes the older unresolved record.
    const superseded = prodFailure
      ? newer.some(o => isSuccess(o.status) || isFailed(o.status))
      : newer.some(o => isSuccess(o.status) || isInProgressStatus(o.status) || isFailed(o.status));

    // Age: prod failures are exempt — they persist until superseded.
    const stale = !prodFailure && now - ts(r.timestamp) > STALE_DEPLOY_MS;

    if (superseded || stale) hidden.add(r.id);
  });

  return hidden;
};

/** Convenience: records with the stale/superseded ones removed. */
export const filterStaleCurrent = (
  records: DeploymentRecord[],
  now: number = Date.now()
): DeploymentRecord[] => {
  const hidden = staleCurrentIds(records, now);
  return hidden.size === 0 ? records : records.filter(r => !r.id || !hidden.has(r.id));
};

/** Rollup status for a set of records (same precedence as legacy tiles). */
export const getOverallStatus = (
  records: DeploymentRecord[],
  config?: NeedsAttentionConfig
): OverallStatusKey => {
  const hasInProgress = records.some(r => isActiveDeploy(r));
  const hasFailed =
    (config
      ? shouldShowInNeedsAttention(records, config)
      : records.some(r => r.status?.toLowerCase() === 'failed')) ||
    records.some(isRollbackProblem);
  const hasPending = records.some(r => r.status?.toLowerCase() === 'pending');
  const allSuccess = records.every(r => r.status?.toLowerCase() === 'success');

  if (hasInProgress) return 'deploying';
  if (hasFailed) return 'failures';
  if (hasPending) return 'pending';
  if (allSuccess) return 'healthy';
  return 'mixed';
};

// ---------------------------------------------------------------------------
// App-level grouping
// ---------------------------------------------------------------------------

export interface AppGroup {
  app: string;
  records: DeploymentRecord[];
  status: OverallStatusKey;
  serviceCount: number;
  envCount: number;
  /** ISO timestamp of the most recent record. */
  lastTimestamp: string | null;
}

export const groupByApp = (
  records: DeploymentRecord[],
  config?: NeedsAttentionConfig
): AppGroup[] => {
  const groups = new Map<string, DeploymentRecord[]>();
  records.forEach(r => {
    const app = r.app || 'Unknown';
    const list = groups.get(app);
    if (list) list.push(r);
    else groups.set(app, [r]);
  });

  return Array.from(groups.entries())
    .map(([app, groupRecords]) => {
      const services = new Set(groupRecords.map(r => r.serviceName || r.service));
      const envs = new Set(groupRecords.map(r => r.envName || r.env));
      const lastTimestamp = groupRecords.reduce<string | null>((max, r) => {
        if (!r.timestamp) return max;
        return !max || ts(r.timestamp) > ts(max) ? r.timestamp : max;
      }, null);
      return {
        app,
        records: groupRecords,
        status: getOverallStatus(groupRecords, config),
        serviceCount: services.size,
        envCount: envs.size,
        lastTimestamp,
      };
    })
    .sort((a, b) => a.app.localeCompare(b.app));
};

// ---------------------------------------------------------------------------
// Service-level grouping (within an app) + env matrix
// ---------------------------------------------------------------------------

/** Status priority for picking the representative record per environment. */
const statusPriority = (status?: string): number => {
  const s = status?.toLowerCase() || '';
  if (IN_PROGRESS.includes(s)) return 3;
  if (s === 'failed') return 2;
  if (s === 'pending') return 1;
  return 0;
};

/** True if `a` should represent the cell over `b` (priority, then recency). */
const outranks = (a: DeploymentRecord, b: DeploymentRecord): boolean => {
  const pa = statusPriority(a.status);
  const pb = statusPriority(b.status);
  if (pa !== pb) return pa > pb;
  return ts(a.timestamp) > ts(b.timestamp);
};

export interface ServiceGroup {
  /** Job id / short service name (right side of `app/`). */
  serviceName: string;
  /** Full raw service string (`type:app/service`) — used for getHistorical. */
  service: string;
  type?: string;
  records: DeploymentRecord[];
  /** Representative record per environment name. */
  byEnv: Map<string, DeploymentRecord>;
  status: OverallStatusKey;
  lastTimestamp: string | null;
}

export const groupServices = (records: DeploymentRecord[]): ServiceGroup[] => {
  const groups = new Map<string, DeploymentRecord[]>();
  records.forEach(r => {
    const name = r.serviceName || r.service || 'unknown';
    // Type-qualified key: two jobs can legitimately share a name (e.g. an
    // `ecs` deploy job and a `terraform` plan job both named "ecs") — a
    // bare-name key silently merged them into one group, keeping only
    // whichever record happened to land first in the array and hiding the
    // other type's tile and history entirely. `serviceHref` and
    // `ServicePage`'s type-less-URL disambiguation already assume two
    // same-named, different-typed groups can coexist; this key is what
    // lets that actually happen.
    const key = `${(r.type || '').toLowerCase()}:${name}`;
    const list = groups.get(key);
    if (list) list.push(r);
    else groups.set(key, [r]);
  });

  return Array.from(groups.entries())
    // The map key is type-qualified (see above) so it stays unique; the
    // group's own `serviceName` field is still the bare name, exactly as
    // every consumer (filters, `serviceHref`, `serviceRecordMatches`)
    // already expects — only pull `type` from a representative record now
    // that a group is guaranteed single-type.
    .map(([, groupRecords]) => {
      const serviceName = groupRecords[0]?.serviceName || groupRecords[0]?.service || 'unknown';
      const byEnv = new Map<string, DeploymentRecord>();
      groupRecords.forEach(r => {
        const env = r.envName || r.env || 'unknown';
        const current = byEnv.get(env);
        if (!current || outranks(r, current)) byEnv.set(env, r);
      });
      const lastTimestamp = groupRecords.reduce<string | null>((max, r) => {
        if (!r.timestamp) return max;
        return !max || ts(r.timestamp) > ts(max) ? r.timestamp : max;
      }, null);
      return {
        serviceName,
        service: groupRecords[0]?.service || '',
        type: groupRecords[0]?.type,
        records: groupRecords,
        byEnv,
        status: getOverallStatus(groupRecords),
        lastTimestamp,
      };
    })
    .sort((a, b) => a.serviceName.localeCompare(b.serviceName));
};

// ---------------------------------------------------------------------------
// Environment ordering — dev → qa/stage → prod, then everything else.
// ---------------------------------------------------------------------------

const ENV_ORDER = ['dev', 'development', 'qa', 'test', 'stage', 'staging', 'prod', 'production'];

export const sortEnvNames = (envs: string[]): string[] => {
  return [...envs].sort((a, b) => {
    const ia = ENV_ORDER.indexOf(a.toLowerCase());
    const ib = ENV_ORDER.indexOf(b.toLowerCase());
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });
};

/**
 * Highest ENV_ORDER rung any of the given env names reached (dev=0 … prod=7).
 * An unrecognized env name doesn't move the rank; -1 if none are recognized.
 */
export const envRank = (envs: string[]): number =>
  envs.reduce((max, env) => Math.max(max, ENV_ORDER.indexOf(env.toLowerCase())), -1);

// ---------------------------------------------------------------------------
// Service table sorting — AppPage's "Service / Type / Environments / Last
// deploy" columns.
// ---------------------------------------------------------------------------

export const VALID_SERVICE_SORT_KEYS = ['service', 'type', 'envs', 'lastDeploy'] as const;
export type ServiceSortKey = typeof VALID_SERVICE_SORT_KEYS[number];

const compareServiceGroups = (a: ServiceGroup, b: ServiceGroup, key: ServiceSortKey): number => {
  switch (key) {
    case 'type': {
      const c = (a.type || '').localeCompare(b.type || '');
      return c !== 0 ? c : a.serviceName.localeCompare(b.serviceName);
    }
    case 'envs': {
      const c = envRank(Array.from(a.byEnv.keys())) - envRank(Array.from(b.byEnv.keys()));
      return c !== 0 ? c : a.serviceName.localeCompare(b.serviceName);
    }
    case 'lastDeploy': {
      const c = ts(a.lastTimestamp ?? undefined) - ts(b.lastTimestamp ?? undefined);
      return c !== 0 ? c : a.serviceName.localeCompare(b.serviceName);
    }
    case 'service':
    default:
      return a.serviceName.localeCompare(b.serviceName);
  }
};

/**
 * Sort a copy of `groups` by `key`/`dir` — never mutates the input array.
 * `desc` negates only the primary comparison (not the serviceName tiebreak,
 * which stays ascending in both directions so ties don't reshuffle).
 */
export const sortServiceGroups = (
  groups: ServiceGroup[],
  key: ServiceSortKey,
  dir: 'asc' | 'desc'
): ServiceGroup[] => {
  const factor = dir === 'desc' ? -1 : 1;
  return [...groups].sort((a, b) => factor * compareServiceGroups(a, b, key));
};

// ---------------------------------------------------------------------------
// Release grouping — app/service/version → latest record per env.
// (Port of ReleasesLens' ReleaseGroup model.)
// ---------------------------------------------------------------------------

export interface ReleaseGroup {
  key: string;
  app: string;
  service: string;
  version: string;
  envMap: Map<string, DeploymentRecord>;
  maxTimestamp: string;
}

export const groupReleases = (records: DeploymentRecord[]): ReleaseGroup[] => {
  const groups = new Map<string, ReleaseGroup>();

  records.forEach(r => {
    if (!r.version) return;
    const key = `${r.app || ''}|${r.serviceName || r.service || ''}|${r.version}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        app: r.app || '',
        service: r.serviceName || r.service || '',
        version: r.version,
        envMap: new Map(),
        maxTimestamp: r.timestamp || '',
      };
      groups.set(key, group);
    }
    const env = r.envName || r.env || 'unknown';
    const current = group.envMap.get(env);
    if (!current || ts(r.timestamp) > ts(current.timestamp)) {
      group.envMap.set(env, r);
    }
    if (r.timestamp && ts(r.timestamp) > ts(group.maxTimestamp)) {
      group.maxTimestamp = r.timestamp;
    }
  });

  return Array.from(groups.values()).sort(
    (a, b) => ts(b.maxTimestamp) - ts(a.maxTimestamp)
  );
};

/**
 * Distinct version strings from a set of records, newest-first, capped at
 * `limit`. The same version can deploy to many envs, so dedup by version and
 * keep the order of its most recent deploy.
 */
export const distinctVersions = (records: DeploymentRecord[], limit: number): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  [...records]
    .sort((a, b) => ts(b.timestamp) - ts(a.timestamp))
    .forEach(r => {
      if (r.version && !seen.has(r.version)) {
        seen.add(r.version);
        out.push(r.version);
      }
    });
  return out.slice(0, limit);
};
